"use client";

import { useState, useEffect, useCallback, useRef } from 'react';
import Image from 'next/image';

const ZOOM_SCALE = 3.5;

export default function ProductLightbox({ images, altText }: { images: string[], altText: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });
  const imgRef = useRef<HTMLImageElement>(null);

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setIsOpen(true);
  };

  const closeLightbox = useCallback(() => {
    setIsOpen(false);
    setZoomed(false);
  }, []);

  const nextImage = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setZoomed(false);
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setZoomed(false);
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // Keyboard navigation in the lightbox (← / → / Esc)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') nextImage();
      else if (e.key === 'ArrowLeft') prevImage();
      else if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, nextImage, prevImage, closeLightbox]);

  // Lupa — follow the cursor when zoomed in
  const handleMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!zoomed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setOrigin({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  const toggleZoom = (e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation();
    if (!zoomed) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setOrigin({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
    }
    setZoomed((z) => !z);
  };

  if (!images || images.length === 0) {
    return <div className="w-full h-full flex items-center justify-center noir-panel" style={{ color: 'var(--muted)' }}>Bez obrázku</div>;
  }

  return (
    <div className="flex flex-col gap-4 w-full h-full">
      {/* Main Image */}
      <div
        className="w-full relative cursor-zoom-in group aspect-[3/4] overflow-hidden rounded-2xl border"
        style={{ background: 'var(--panel)', borderColor: 'var(--line)' }}
        onClick={() => openLightbox(currentIndex)}
      >
        <Image
          src={images[currentIndex]}
          alt={altText}
          fill
          sizes="(max-width:768px) 100vw, 42vw"
          priority
          className="object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <span className="font-mono uppercase text-white px-4 py-2 rounded-full text-xs backdrop-blur-sm" style={{ letterSpacing: '.12em', background: 'rgba(11,11,13,.5)', border: '1px solid var(--line-strong)' }}>Zvětšit</span>
        </div>

        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(e); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 backdrop-blur-md text-white p-2 rounded-full transition-opacity opacity-0 group-hover:opacity-100"
              style={{ background: 'rgba(11,11,13,.5)' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(e); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 backdrop-blur-md text-white p-2 rounded-full transition-opacity opacity-0 group-hover:opacity-100"
              style={{ background: 'rgba(11,11,13,.5)' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
            </button>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className="relative shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all"
              style={{
                borderColor: currentIndex === idx ? 'var(--gold)' : 'transparent',
                opacity: currentIndex === idx ? 1 : 0.55,
              }}
            >
              <Image src={img} alt={`${altText} ${idx + 1}`} width={80} height={80} className="object-cover w-full h-full" />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 transition-opacity"
          style={{ background: 'rgba(6,6,8,.96)' }}
          onClick={closeLightbox}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            ref={imgRef}
            src={images[currentIndex]}
            alt={altText}
            onClick={toggleZoom}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => zoomed && setOrigin({ x: 50, y: 50 })}
            className="max-w-full max-h-full object-contain rounded-lg select-none"
            style={{
              cursor: zoomed ? 'zoom-out' : 'zoom-in',
              transform: zoomed ? `scale(${ZOOM_SCALE})` : 'scale(1)',
              transformOrigin: `${origin.x}% ${origin.y}%`,
              transition: 'transform .25s ease',
            }}
            draggable={false}
          />

          <button
            className="absolute top-6 right-6 text-white rounded-full p-3 transition-colors backdrop-blur-md"
            style={{ background: 'rgba(255,255,255,.1)' }}
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>

          {/* Zoom toggle (lupa) */}
          <button
            className="absolute top-6 right-24 text-white rounded-full p-3 transition-colors backdrop-blur-md"
            style={{ background: zoomed ? 'var(--gold)' : 'rgba(255,255,255,.1)', color: zoomed ? '#0b0b0d' : '#fff' }}
            onClick={(e) => {
              e.stopPropagation();
              setZoomed((z) => !z);
              setOrigin({ x: 50, y: 50 });
            }}
            aria-label={zoomed ? 'Zmenšit' : 'Zvětšit (lupa)'}
            title={zoomed ? 'Zmenšit' : 'Zvětšit (lupa)'}
          >
            {zoomed ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM8 10h6"></path></svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM11 8v6M8 11h6"></path></svg>
            )}
          </button>

          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(e); }}
                className="absolute left-6 top-1/2 -translate-y-1/2 backdrop-blur-md text-white p-4 rounded-full transition-colors"
                style={{ background: 'rgba(255,255,255,.1)' }}
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(e); }}
                className="absolute right-6 top-1/2 -translate-y-1/2 backdrop-blur-md text-white p-4 rounded-full transition-colors"
                style={{ background: 'rgba(255,255,255,.1)' }}
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
              </button>

              <div className="absolute bottom-10 left-1/2 -translate-x-1/2 font-mono text-white px-4 py-2 rounded-full backdrop-blur-md text-sm" style={{ background: 'rgba(255,255,255,.1)' }}>
                {currentIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
